/**
 * API trigger rate-limit "Options" — array of rows with:
 * - `intervalValue` — window order (1, 2, 3…); sequential position in the list
 * - `interval` — unit (second | minute | hour | day | month)
 * - `limit` — max requests per window
 * Persisted on `triggerSpec.options` (camelCase keys in JSON).
 */

export const RATE_LIMIT_INTERVAL_VALUES = [
  'second',
  'minute',
  'hour',
  'day',
  'month'
] as const;

export type RateLimitInterval = (typeof RATE_LIMIT_INTERVAL_VALUES)[number];

/** One row in Configure API form (includes UI-only `id`). */
export type RateLimitOptionRow = {
  id: string;
  /** Window order in the list (1-based, sequential, no gaps). */
  intervalValue: number;
  interval: RateLimitInterval;
  limit: number;
};

function newRowId() {
  return (
    globalThis.crypto?.randomUUID?.() ?? `rlo-${Date.now()}-${Math.random()}`
  );
}

function clampPositiveInt(raw: unknown, fallback: number): number {
  if (typeof raw !== 'number' || !Number.isFinite(raw) || Number.isNaN(raw)) {
    return fallback;
  }
  const t = Math.trunc(raw);
  return t >= 1 ? t : fallback;
}

/** Assigns intervalValue = index + 1 for every row in display order. */
export function reindexRateLimitOptionRows(
  rows: RateLimitOptionRow[]
): RateLimitOptionRow[] {
  return rows.map((row, idx) => ({
    ...row,
    intervalValue: idx + 1
  }));
}

/** Moves one row to a 1-based window position and reindexes the list. */
export function moveRateLimitOptionRowToPosition(
  rows: RateLimitOptionRow[],
  rowId: string,
  targetPosition: number
): RateLimitOptionRow[] {
  if (rows.length === 0) {
    return rows;
  }
  const currentIndex = rows.findIndex(r => r.id === rowId);
  if (currentIndex === -1) {
    return reindexRateLimitOptionRows(rows);
  }
  const clamped = Math.max(
    1,
    Math.min(rows.length, Math.trunc(targetPosition))
  );
  const next = [...rows];
  const [row] = next.splice(currentIndex, 1);
  next.splice(clamped - 1, 0, row);
  return reindexRateLimitOptionRows(next);
}

export function createDefaultRateLimitOptionRow(
  overrides?: Partial<
    Pick<RateLimitOptionRow, 'interval' | 'intervalValue' | 'limit'>
  >
): RateLimitOptionRow {
  return {
    id: newRowId(),
    intervalValue: clampPositiveInt(overrides?.intervalValue ?? 1, 1),
    interval: overrides?.interval ?? 'second',
    limit:
      typeof overrides?.limit === 'number' &&
      Number.isFinite(overrides.limit) &&
      overrides.limit >= 1
        ? overrides.limit
        : 100
  };
}

function normalizeInterval(raw: unknown): RateLimitInterval {
  if (typeof raw !== 'string') {
    return 'second';
  }
  const v = raw.trim().toLowerCase();
  const aliases: Record<string, RateLimitInterval> = {
    seconds: 'second',
    minutes: 'minute',
    hours: 'hour',
    days: 'day',
    months: 'month'
  };
  const mapped = aliases[v] ?? (v as RateLimitInterval);
  return RATE_LIMIT_INTERVAL_VALUES.includes(mapped as RateLimitInterval)
    ? (mapped as RateLimitInterval)
    : 'second';
}

/** Parse `triggerSpec.options` (or connector jsonExample) into form rows. */
export function parseRateLimitOptionsFromTriggerSpec(
  raw: unknown
): RateLimitOptionRow[] {
  let coerced: unknown = raw;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed || trimmed === '[]') {
      return [createDefaultRateLimitOptionRow()];
    }
    try {
      coerced = JSON.parse(trimmed);
    } catch {
      return [createDefaultRateLimitOptionRow()];
    }
  }
  if (!Array.isArray(coerced) || coerced.length === 0) {
    return [createDefaultRateLimitOptionRow()];
  }
  const items = [...coerced];
  const mapped = items
    .filter(
      (item): item is Record<string, unknown> =>
        Boolean(item) && typeof item === 'object' && !Array.isArray(item)
    )
    .map((o, sourceIndex) => {
      const interval = normalizeInterval(o.interval ?? o.Interval);
      const intervalValRaw =
        o.intervalValue ?? o.IntervalValue ?? o.quantity ?? o.Quantity;
      const intervalValue = clampPositiveInt(intervalValRaw, 1);

      const limitRaw = o.limit ?? o.Limit;
      const limitNum = Number(limitRaw);
      const limit =
        Number.isFinite(limitNum) && !Number.isNaN(limitNum)
          ? Math.trunc(limitNum)
          : 0;
      const limitClamped = limit >= 1 ? limit : 100;
      const row: RateLimitOptionRow = {
        id: newRowId(),
        intervalValue,
        interval,
        limit: limitClamped
      };
      return { row, sourceIndex };
    });
  const withOrder = [...mapped].sort((a, b) => {
    const byOrder = a.row.intervalValue - b.row.intervalValue;
    if (byOrder !== 0) {
      return byOrder;
    }
    return a.sourceIndex - b.sourceIndex;
  });

  const rows = withOrder.map(({ row }) => row);

  return rows.length > 0
    ? reindexRateLimitOptionRows(rows)
    : [createDefaultRateLimitOptionRow()];
}

/** Shape written to `triggerSpec.options`. */
export type RateLimitOptionSpec = {
  intervalValue: number;
  interval: RateLimitInterval;
  limit: number;
};

export function rateLimitOptionsToTriggerSpec(
  rows: RateLimitOptionRow[]
): RateLimitOptionSpec[] {
  return reindexRateLimitOptionRows(rows).map(
    ({ intervalValue, interval, limit }) => ({
      intervalValue,
      interval,
      limit:
        typeof limit === 'number' &&
        Number.isFinite(limit) &&
        !Number.isNaN(limit)
          ? Math.max(1, Math.trunc(limit))
          : 1
    })
  );
}

/** For dirty compare: ignore row ids; preserve list order with sequential window length. */
export function normalizeRateLimitOptionsForCompare(
  rows: RateLimitOptionRow[]
): RateLimitOptionSpec[] {
  return reindexRateLimitOptionRows(rows).map(
    ({ intervalValue, interval, limit }) => ({
      intervalValue,
      interval,
      limit:
        typeof limit === 'number' &&
        Number.isFinite(limit) &&
        !Number.isNaN(limit)
          ? Math.max(1, Math.trunc(limit))
          : 1
    })
  );
}
